import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, filter } from 'rxjs';
import { AvailableFormsService } from './available-forms.service';
import { MenuItem } from 'app/models/MenuItem';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * Service for managing the page title within the application.
 *
 * Automatically sets the page title based on the current route by matching
 * against available menu items. Also provides manual override capability.
 * Consumers can subscribe to `title$` to react to title changes.
 *
 * @example
 * // Get the current page title
 * const currentTitle = pageTitleService.getPageTitle();
 *
 * // Subscribe to title changes
 * pageTitleService.title$.subscribe(title => console.log(title));
 *
 * // Manual override (optional)
 * pageTitleService.setPageTitle('Custom Title');
 */
@Injectable({
	providedIn: 'root',
})
export class PageTitleService {
	private titleSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
	title$ = this.titleSubject.asObservable();

	constructor(private readonly router: Router, private readonly availableFormsService: AvailableFormsService) {
		// Set title on initial load
		this.setTitleFromRoute();

		// Listen for all navigation
		this.router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				takeUntilDestroyed()
			)
			.subscribe(() => {
				this.setTitleFromRoute();
			});
	}

	setPageTitle(newTitle?: string) {
		this.titleSubject.next(newTitle || '');
	}
	getPageTitle() {
		return this.titleSubject.value;
	}

	private setTitleFromRoute(): void {
		const currRoute = this.router.url;
		const currMenuItem = this.findMenuItemByRoute(currRoute, this.availableFormsService.getAllMenuItems());
		if (currMenuItem?.section) {
			this.titleSubject.next(currMenuItem.section);
		}
	}

	private findMenuItemByRoute(url: string, items: MenuItem[]): MenuItem | null {
		const cleanUrl = url.split('?')[0];
		for (const item of items) {
			if (item.route) {
				if (cleanUrl === item.route || cleanUrl.includes(item.route)) {
					return item;
				}
			}
			if (item.children && item.children.length > 0) {
				const found = this.findMenuItemByRoute(url, item.children);
				if (found) {
					return found;
				}
			}
		}
		return null;
	}
}
